import { get, patch, deleteRequest, postFormData } from './apiClient';

/**
 * The vendor item memory — what the system has settled about each supplier's
 * products, learned from the vendor-invoice review grid.
 *
 * Extraction misreads the same layouts the same way on every invoice, so a
 * reviewer answers the same question about the same product every time. The
 * memory records the answer once and replays it: a blank Pack Unit or Units per
 * Case is filled in, and a name a reviewer cleaned up stays cleaned up.
 *
 * This module is the way to SEE and CORRECT that memory. It matters more than
 * it looks: one wrong answer would otherwise replay onto every future invoice
 * for that product, and the only way to reach it would be to find an invoice
 * that happens to carry the line.
 */

/** How strong the evidence behind a remembered value is. */
export type MemoryWeight = 'typed' | 'confirmed' | 'seen';

/**
 * Which rung of the identity ladder an alias was recorded on.
 *
 * `settled` is the item name a REVIEWER typed. It exists because rungs made out
 * of the printed description only work for a supplier whose invoices are
 * printed: a handwritten one comes back misread differently every time
 * ("16 Bals Chicken Thigh" then "16 Beds Chicken Thigh" — both *Bnls*), so the
 * name a person types is the only stable thing on the line.
 */
export type MemoryAliasKind = 'code' | 'unpaddedCode' | 'settled' | 'description' | 'name';

export interface LearnedValue {
  value: string | number | boolean | null;
  weight: MemoryWeight;
  /** Consecutive invoices that agreed with this value. */
  sightings: number;
  firstSeenAt?: string;
  lastSeenAt?: string;
  lastInvoiceNumber?: string;
}

/**
 * Only fields that are a property of the PRODUCT. Quantities, prices, totals,
 * tax amounts and expiry dates belong to one delivery and are never learned —
 * there is no field here for them to live in.
 */
export interface LearnedFields {
  itemName?: LearnedValue | null;
  itemCode?: LearnedValue | null;
  splitUow?: LearnedValue | null;
  splitPackCount?: LearnedValue | null;
  splitUnitSize?: LearnedValue | null;
  splitItemUow?: LearnedValue | null;
  taxRate?: LearnedValue | null;
  /**
   * Is this product billed by ACTUAL weight — meat, poultry, fish?
   *
   * A property of the product, which is why it lives here: "boxes of this never
   * weigh the same" stays true after the delivery it was learned from. It
   * decides whether the Master Catalogue row may state a weight per piece at
   * all, so correcting it here changes what every future quotation off that row
   * is priced against.
   */
  isCatchWeight?: LearnedValue | null;
}

export type LearnedFieldKey = keyof LearnedFields;

export interface VendorItemProfile {
  _id: string;
  vendorId: string;
  vendorName?: string;
  /** The identity this product was filed under. Lookups go through `aliases`. */
  itemKey: string;
  itemKeyKind: MemoryAliasKind;
  displayName?: string;
  /** Every spelling this product has been recognised by. Append-only. */
  aliases: { kind: MemoryAliasKind; key: string }[];
  fields: LearnedFields;
  /** Alerting only — never replayed onto a line. A remembered price is a fabricated price. */
  lastPrice?: { value: number | null; currency: string | null; seenAt: string | null; invoiceNumber?: string };
  invoicesSeen: number;
  lastSeenAt?: string;
}

export async function listVendorItemMemory(params: {
  vendorId: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  data: VendorItemProfile[];
  meta: { page: number; limit: number; total: number };
}> {
  const qs = new URLSearchParams({ vendorId: params.vendorId });
  if (params.search) qs.set('search', params.search);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  return get(`/vendor-item-memory?${qs.toString()}`);
}

/**
 * Correct one remembered answer.
 *
 * Pass `null` (or an empty string) to clear just that field, leaving the rest
 * of what we know about the product intact. The server stores a hand-typed
 * value at the strongest weight and resets its sighting count — otherwise a
 * long run of agreeing misreads would outrank the correction meant to end it.
 */
export async function setVendorItemMemoryField(
  profileId: string,
  field: LearnedFieldKey,
  value: string | number | boolean | null
): Promise<{ success: boolean; data: { profile: VendorItemProfile } }> {
  return patch(`/vendor-item-memory/${profileId}/field`, { field, value });
}

/** What a code-list import found, and (when not a preview) what it wrote. VI-10. */
export interface CodeListImportReport {
  dryRun: boolean;
  fileName: string;
  sheetName: string;
  /** 1-based, as the spreadsheet numbers it. */
  headerRow: number;
  /** Column letters. */
  columns: { code: string; description: string; packaging: string | null };
  /** The first row read back, so a column picked wrongly shows in the values. */
  sample: { row: number; code: string; description: string; packaging: string } | null;
  rowsRead: number;
  created: number;
  updated: number;
  unchanged: number;
  /** Answers a person typed or checked, which the list did not overwrite. */
  keptStaffAnswers: { code: string; field: string; kept: unknown; fromList: unknown }[];
  /** The same code more than once. Conflicting ones are skipped, identical ones imported once. */
  duplicates: { code: string; rows: number[]; identical: boolean }[];
  /** Codes that differ only by leading zeros: both kept apart, no unpadded twin. */
  collisions: { code: string; otherCode: string }[];
  packagingNotRead: { row: number; code: string; packaging: string }[];
  skipped: { row: number; reason: string }[];
}

/**
 * Import a supplier's code -> description (-> packaging) list into the memory.
 * `dryRun` previews without writing; the screen always previews first.
 */
export async function importVendorItemCodeList(
  vendorId: string,
  file: File,
  dryRun: boolean
): Promise<{ success: boolean; data: CodeListImportReport }> {
  const formData = new FormData();
  formData.append('vendorId', vendorId);
  formData.append('dryRun', dryRun ? 'true' : 'false');
  formData.append('file', file);
  return postFormData('/vendor-item-memory/import', formData);
}

/**
 * Forget a product entirely. The next confirmed invoice simply learns it again
 * from scratch, which is what somebody clicking "Forget" is asking for.
 */
export async function forgetVendorItemMemory(
  profileId: string
): Promise<{ success: boolean; data: { forgotten: boolean; displayName?: string } }> {
  return deleteRequest(`/vendor-item-memory/${profileId}`);
}
